import { AddOrRemoveMainContainerClassesType } from "@whatslly/types/AddOrRemoveMainContainerClassesType";
import { UserType } from "@whatslly/types/User";
import { ContactFilterObjectType, RefreshStrategyType } from "@whatslly/types/ContactFilterObject";
import { BusinessLabel, GetDataType } from "@whatslly/types/instant-messaging/WhatsappTypes";
import { CreateLeadResponseType } from "@whatslly/types/CreateLeadResponse";
import { CreateContactResponseType } from "@whatslly/types/CreateContactResponse";
import { SelectedContactType } from "@whatslly/types/SelectedContactType";
import { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import { IMChannelType } from "@whatslly/types/channelTypes";
import { InstantMessagingMessage } from "@whatslly/types/instant-messaging/InstantMessagingMessage";
import type { SelfWaNotificationSoundUploadResponseType } from "@whatslly/types/SelfWaNotificationSound";

export const WEB_SCRIPT_COMMANDS_PREFIX = "whatslly-web-script";

export const WebScriptCommands = {
  ABORT_TRANSACTION: `${WEB_SCRIPT_COMMANDS_PREFIX}:abort-transaction`,
  OPEN_HIDDEN_WIDGET: `${WEB_SCRIPT_COMMANDS_PREFIX}:open-hidden-widget`,
  OPEN_WIDGET: `${WEB_SCRIPT_COMMANDS_PREFIX}:open-widget`,
  CLOSE_WIDGET: `${WEB_SCRIPT_COMMANDS_PREFIX}:close-widget`,
  REFRESH_PERSON: `${WEB_SCRIPT_COMMANDS_PREFIX}:refresh-person`,
  USER_REFRESHED: `${WEB_SCRIPT_COMMANDS_PREFIX}:user-refreshed`,
  OPEN_WAP_CHAT: `${WEB_SCRIPT_COMMANDS_PREFIX}:open-wap-chat`,
  START_CAPTURE_CHAT_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:start-capture-chat-messages`,
  STOP_CAPTURE_CHAT_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:stop-capture-chat-messages`,
  UPDATE_AUTO_SYNC_STATUS: `${WEB_SCRIPT_COMMANDS_PREFIX}:update-auto-sync-status`,
  RESPONSE: `${WEB_SCRIPT_COMMANDS_PREFIX}:web-script-response`,
  ERROR: `${WEB_SCRIPT_COMMANDS_PREFIX}:web-script-error`,
  PASTE_IN_WHATSAPP: `${WEB_SCRIPT_COMMANDS_PREFIX}:paste-in-whatsapp`,
  CHANGE_VISIBLE_CONTACTS: `${WEB_SCRIPT_COMMANDS_PREFIX}:change-visible-contacts`,
  ADD_OR_REMOVE_MAIN_CONTAINER_CLASSES: `${WEB_SCRIPT_COMMANDS_PREFIX}:add-or-remove-main-container-classes`,
  TOGGLE_FILTERS: `${WEB_SCRIPT_COMMANDS_PREFIX}:toggle-filters`,
  COMMIT_SELECTED_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:commit-selected-messages`,
  LOGOUT: `${WEB_SCRIPT_COMMANDS_PREFIX}:logout`,
  CHANGE_WAP_BLUR: `${WEB_SCRIPT_COMMANDS_PREFIX}:change-wap-blur`,
  TOGGLE_TEMPLATES: `${WEB_SCRIPT_COMMANDS_PREFIX}:toggle-templates`,
  UPDATE_STATUS_BAR_ITEM: `${WEB_SCRIPT_COMMANDS_PREFIX}:update-status-bar-item`,
  SHOW_FILTERED_CHATS_AMOUNT: `${WEB_SCRIPT_COMMANDS_PREFIX}:show-filtered-chats-amount`,
  COUNT_FILTER_WITHOUT_APPLYING: `${WEB_SCRIPT_COMMANDS_PREFIX}:count-filter-without-applying`,
  GET_SUPPORTED_FILTERS: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-supported-filters`,
  GET_CONTACTS_COUNTRY_CODES: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-contacts-country-codes`,
  GET_BUSINESS_LABELS: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-business-labels`,
  GET_IS_WHATSAPP_BUSINESS: `${WEB_SCRIPT_COMMANDS_PREFIX}:is-whatsapp-business`,
  UPDATE_GROUP_STATE: `${WEB_SCRIPT_COMMANDS_PREFIX}:update-group-state`,
  PERSON_CREATED: `${WEB_SCRIPT_COMMANDS_PREFIX}:person-created`,
  GET_CONNECTED_STATUS: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-connected-status`,
  INSTANT_MESSAGE_LOGOUT: `${WEB_SCRIPT_COMMANDS_PREFIX}:instant-message-logout`,
  AUTH: `${WEB_SCRIPT_COMMANDS_PREFIX}:auth`,
  GET_ALL_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-all-messages`,
  LOAD_EARLIER_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:load-earlier-messages`,
  CHANGE_LANG: `${WEB_SCRIPT_COMMANDS_PREFIX}:change-lang`,
  GET_SELECTED_CONTACT: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-selected-contact`,
  GET_ME: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-me`,
  SYNC_ALL_CONTACT_MESSAGES: `${WEB_SCRIPT_COMMANDS_PREFIX}:sync-all-contact-messages`,
  SYNC_ALL_GROUPS: `${WEB_SCRIPT_COMMANDS_PREFIX}:sync-all-groups`,
  DLP_POLICIES_CHANGED: `${WEB_SCRIPT_COMMANDS_PREFIX}:dlp-policies-changed`,
  SURVEILLANCE_POLICIES_CHANGED: `${WEB_SCRIPT_COMMANDS_PREFIX}:surveillance-policies-changed`,
  KEYWORD_POLICIES_CHANGED: `${WEB_SCRIPT_COMMANDS_PREFIX}:keyword-policies-changed`,
  SHOW_FRAUD_DETECTED: `${WEB_SCRIPT_COMMANDS_PREFIX}:show-fraud-detected`,
  SHOW_FRAUD_DETECTED_TELEGRAM: `${WEB_SCRIPT_COMMANDS_PREFIX}:show-fraud-detected-telegram`,
  INSTANT_MESSAGING_GET_CHATS_LABELS: `${WEB_SCRIPT_COMMANDS_PREFIX}:instant-messaging-get-chats-label`,
  INSTANT_MESSAGING_ADD_LABEL: `${WEB_SCRIPT_COMMANDS_PREFIX}:instant-messaging-add-org-label`,
  INSTANT_MESSAGING_DELETE_LABEL: `${WEB_SCRIPT_COMMANDS_PREFIX}:instant-messaging-delete-org-label`,
  ON_COPY: `${WEB_SCRIPT_COMMANDS_PREFIX}:on-copy`,
  CHECK_DLP: `${WEB_SCRIPT_COMMANDS_PREFIX}:check-dlp`,
  SET_WA_CUSTOM_NOTIFICATION_SOUND: `${WEB_SCRIPT_COMMANDS_PREFIX}:set-wa-custom-notification-sound`,
  GET_WA_CUSTOM_NOTIFICATION_SOUND_STATUS: `${WEB_SCRIPT_COMMANDS_PREFIX}:get-wa-custom-notification-sound-status`,
  REFRESH_WA_NOTIFICATION_TONE_PLAYBACK: `${WEB_SCRIPT_COMMANDS_PREFIX}:refresh-wa-notification-tone-playback`,
} as const;

export type WebScriptCommandsKeysType = keyof typeof WebScriptCommands;

export type WebScriptCommandsTypes = {
  [WebScriptCommands.ABORT_TRANSACTION]: never;
  [WebScriptCommands.RESPONSE]: never;
  [WebScriptCommands.ERROR]: never;
  [WebScriptCommands.ADD_OR_REMOVE_MAIN_CONTAINER_CLASSES]: (req: AddOrRemoveMainContainerClassesType) => void;
  [WebScriptCommands.CLOSE_WIDGET]: () => void;
  [WebScriptCommands.OPEN_WIDGET]: () => void;
  [WebScriptCommands.OPEN_HIDDEN_WIDGET]: () => void;
  [WebScriptCommands.UPDATE_STATUS_BAR_ITEM]: (req: {
    parentItemId: string;
    success: boolean;
    prevSelectedItemValue: string;
    selectedItemValue: string;
  }) => void;
  [WebScriptCommands.CHANGE_WAP_BLUR]: (req: { newState: boolean; channelName?: IMChannelType }) => void;
  [WebScriptCommands.OPEN_WAP_CHAT]: (req: { phone: string; forceOpenChat?: boolean }) => void;
  [WebScriptCommands.PASTE_IN_WHATSAPP]: (req: { text: string }) => void;
  [WebScriptCommands.TOGGLE_FILTERS]: (req: { newFiltersState: boolean }) => void;
  [WebScriptCommands.LOGOUT]: () => void;
  [WebScriptCommands.AUTH]: (req: { user: UserType | undefined }) => void;
  [WebScriptCommands.PERSON_CREATED]: (req: {
    person: CreateContactResponseType | CreateLeadResponseType;
    selectedContact: SelectedContactType;
  }) => void;
  [WebScriptCommands.USER_REFRESHED]: (req: { user: UserType }) => void;
  [WebScriptCommands.REFRESH_PERSON]: (req: { personId?: string; forceRefresh?: boolean; skipDefault?: boolean; skipCache?: boolean }) => void;
  [WebScriptCommands.GET_ALL_MESSAGES]: (chatId?: GetDataType) => InstantMessagingMessage[] | undefined;
  [WebScriptCommands.LOAD_EARLIER_MESSAGES]: () => void;
  [WebScriptCommands.INSTANT_MESSAGE_LOGOUT]: () => void;
  [WebScriptCommands.GET_CONNECTED_STATUS]: () => { connected: false } | { connected: true; userPhone: string };
  [WebScriptCommands.CHANGE_VISIBLE_CONTACTS]: (req: {
    filter: ContactFilterObjectType;
    refreshStrategy?: RefreshStrategyType;
    refreshOnEvent?: boolean;
  }) => void;
  [WebScriptCommands.SHOW_FILTERED_CHATS_AMOUNT]: (data: { showAmount: boolean }) => string[] | undefined;
  [WebScriptCommands.COUNT_FILTER_WITHOUT_APPLYING]: (filter: ContactFilterObjectType) => number;
  [WebScriptCommands.GET_CONTACTS_COUNTRY_CODES]: () => { countryCode: string; count: number }[] | undefined;
  [WebScriptCommands.GET_SUPPORTED_FILTERS]: () => Array<keyof ContactFilterObjectType> | undefined;
  [WebScriptCommands.GET_BUSINESS_LABELS]: () => Array<BusinessLabel & { count: number }> | undefined;
  [WebScriptCommands.GET_IS_WHATSAPP_BUSINESS]: () => boolean;
  [WebScriptCommands.START_CAPTURE_CHAT_MESSAGES]: () => void;
  [WebScriptCommands.STOP_CAPTURE_CHAT_MESSAGES]: () => void;
  [WebScriptCommands.UPDATE_AUTO_SYNC_STATUS]: (req: { newStatus: "auto" | "selective" | "off" }) => void;
  [WebScriptCommands.COMMIT_SELECTED_MESSAGES]: () => void;
  [WebScriptCommands.UPDATE_GROUP_STATE]: (req: WhatsllyGroupType) => void;
  [WebScriptCommands.TOGGLE_TEMPLATES]: () => void;
  [WebScriptCommands.CHANGE_LANG]: (data: { lang: string }) => void;
  [WebScriptCommands.GET_SELECTED_CONTACT]: () => SelectedContactType | undefined;
  [WebScriptCommands.GET_ME]: () => { server: string; phone: string } | undefined;
  [WebScriptCommands.SYNC_ALL_CONTACT_MESSAGES]: () => void;
  [WebScriptCommands.SYNC_ALL_GROUPS]: () => void;
  [WebScriptCommands.DLP_POLICIES_CHANGED]: () => Promise<void>;
  [WebScriptCommands.SURVEILLANCE_POLICIES_CHANGED]: () => Promise<void>;
  [WebScriptCommands.KEYWORD_POLICIES_CHANGED]: () => Promise<void>;
  [WebScriptCommands.SHOW_FRAUD_DETECTED]: (messageId: string) => void;
  [WebScriptCommands.SHOW_FRAUD_DETECTED_TELEGRAM]: (messageId: string) => void;
  [WebScriptCommands.INSTANT_MESSAGING_GET_CHATS_LABELS]: () => Map<string, number>;
  [WebScriptCommands.INSTANT_MESSAGING_ADD_LABEL]: (labelName: string) => Promise<void>;
  [WebScriptCommands.INSTANT_MESSAGING_DELETE_LABEL]: (labelId: string) => Promise<boolean>;
  [WebScriptCommands.ON_COPY]: (text: string) => void;
  [WebScriptCommands.CHECK_DLP]: (data: { content: string; attachments?: { data: string; name: string; type: string }[] }) => Promise<{
    blocked: boolean;
    policyName?: string;
    isCorruptedOrEncryptedFile?: boolean;
    isAudioTranscriptionFailed?: boolean;
  }>;
  [WebScriptCommands.SET_WA_CUSTOM_NOTIFICATION_SOUND]: (
    req: { base64: string; mimeType: string; fileName?: string; payloadByteLength: number } | { clear: true }
  ) => Promise<SelfWaNotificationSoundUploadResponseType | void>;
  [WebScriptCommands.GET_WA_CUSTOM_NOTIFICATION_SOUND_STATUS]: () => Promise<{
    hasCustom: boolean;
    displayName: string | null;
  }>;
  [WebScriptCommands.REFRESH_WA_NOTIFICATION_TONE_PLAYBACK]: (req?: { restoreDefault?: boolean }) => void;
};
